import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  setEmployeeDetail,
  setAccountTypes,
  setLatestCos,
  setLatestTrl,
  setLatestCwa,
} from "../../state/slices/accountDetailSlice";
import { normalizeEmployeeStatus, toUiPosition } from "../../utils/generalUtils";
import {
  setBulletins,
  setLoginLoaded,
  setMaintenanceBlocked,
} from "../../state/slices/generalSlice";

const AuthHandler = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Pick up token from URL (post-OAuth redirect) or localStorage
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");

    if (urlToken) {
      localStorage.setItem("api_token", urlToken);
      window.history.replaceState({}, "", "/");
    }

    const token = urlToken || localStorage.getItem("api_token");
    if (!token) return;

    fetch(`${process.env.REACT_APP_API_URL}/auth/me`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        if (res.status === 503) {
          const body = await res.json().catch(() => null);
          if (body?.maintenance) {
            dispatch(setMaintenanceBlocked(true));
            dispatch(setLoginLoaded(true));
            return null;
          }
        }

        if (!res.ok) {
          // Token invalid or expired — force re-login
          localStorage.removeItem("api_token");
          window.location.href = "/";
          return null;
        }
        return res.json();
      })
      .then((data) => {
        if (!data) return;

        const positions = (data?.details?.positions || []).map((position) =>
          toUiPosition(position)
        );
        const normalizedDetails = normalizeEmployeeStatus(data?.details || {});
        const adminPosition = data?.details?.admin_roles || [];
        const navBarItems = new Set();

        const bulletins = data?.ebulletin || [];

        const trl = data?.latest_submission?.filter(
          (s) => s.document_type === "trl"
        );
        const cos = data?.latest_submission?.filter(
          (s) => s.document_type === "cos"
        );
        const cwa = data?.latest_submission?.filter(
          (s) => s.document_type === "cwa"
        );

        positions.forEach(({ is_approver, is_faculty, approver_id }) => {
          if (is_approver) {
            navBarItems.add("approver");
            if (approver_id && approver_id !== 0) navBarItems.add("employee");
          } else {
            navBarItems.add("employee");
          }

          if (is_faculty) navBarItems.add("faculty");
        });

        const spectatorAccesses = data?.details?.spectator_accesses || [];
        if (spectatorAccesses.length > 0) {
          navBarItems.add("spectator");
          navBarItems.add("employee");
        }

        adminPosition.forEach(({ role }) => {
          switch (role) {
            case "xadmin":
              navBarItems.add("superadmin");
              break;
            case "positionsAdmin":
              navBarItems.add("positionsAdmin");
              break;
            case "ebulAdmin":
              navBarItems.add("ebulAdmin");
              break;
            case "fsrAdmin":
              navBarItems.add("fsrAdmin");
              break;
            default:
              break;
          }
        });

        dispatch(setEmployeeDetail({ ...normalizedDetails, positions }));
        dispatch(setAccountTypes([...navBarItems]));
        dispatch(setLoginLoaded(true));
        dispatch(setLatestCos(cos));
        dispatch(setLatestTrl(trl));
        dispatch(setLatestCwa(cwa));
        dispatch(setBulletins(bulletins));
      })
      .catch((error) => {
        console.error("Error loading user data:", error);
        localStorage.removeItem("api_token");
      });
  }, [dispatch]);

  return null;
};

export default AuthHandler;
